import {useCallback, useEffect, useRef} from 'react';
import {Box, useDevice, useViewport} from '@treatwell/ui';
import {useAppSelector} from '../../hooks/store';
import {
  selectSelectedDate,
  selectSelectedStartTime,
  selectTimeslots,
  selectTimeslotsInitialDate,
} from '../../store/slices/selectors';
import {
  dispatchDateSelect,
  dispatchStartTimeSelect,
} from '../../store/slices/dispatch';
import {trackSelectDateTime} from '../../utils/gtmTracking';
import {TimeSelectAction} from '../../store/slices/reducers';
import {useStore} from 'react-redux';
import clsx from 'clsx';
import styles from './CalendarNavigation.module.css';
import {TimeSlotList} from '../TimeSlotList';
import {trackDateSelectedEvent} from './trackDateSelectedEvent';
import {DayListNavigation} from '../DayListNavigation/DayListNavigation';
import {isAvailabiltyRoute} from '../../utils/isRoute';
import {useUpdateDateAndTimeQueryParam} from '../../hooks/useUpdateDateAndTimeQueryParam';

export const CalendarNavigation = () => {
  if (isAvailabiltyRoute()) {
    // disabling rules of hooks rule for the following hook as
    // we can be sure this if statement is either true or false depending on the route and
    // there shouldn't be a risk that the order of the hooks will change on subsequent renders
    useUpdateDateAndTimeQueryParam(); // eslint-disable-line react-hooks/rules-of-hooks
  }

  const selectedDate = useAppSelector(selectSelectedDate);
  const timeslots = useAppSelector(selectTimeslots);
  const selectedStartTime = useAppSelector(selectSelectedStartTime);
  const initialDateTimeslots = useAppSelector(selectTimeslotsInitialDate);
  const startTimeIndex =
    timeslots?.findIndex((timeslot) => {
      return timeslot.startTime === selectedStartTime;
    }) || 0;
  const {isMobile} = useDevice();
  const store = useStore();
  const isSmallViewport = useViewport({device: ['mobile', 'tablet']});
  const calendarNavRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    trackSelectDateTime({
      date: selectedDate,
    });
    // Scroll to place date nav at top (sticky) if scroll position is below, when switching dates.
    if (
      calendarNavRef.current &&
      calendarNavRef.current.getBoundingClientRect().top < 0
    ) {
      calendarNavRef.current.scrollIntoView(true);
    }
  }, [selectedDate]);

  const handleDispatchDateSelect = useCallback((date: string) => {
    dispatchDateSelect({
      date,
    });
  }, []);

  const handleDaySelect = useCallback(
    (date: string) => {
      handleDispatchDateSelect(date);
    },
    [handleDispatchDateSelect],
  );

  const handleTimeSlotSelect = ({
    startTime,
    hasDiscount,
    totalPrice,
    originalPrice,
  }: TimeSelectAction) => {
    dispatchStartTimeSelect({
      startTime,
      hasDiscount,
      totalPrice,
      originalPrice,
    });
  };

  return (
    <div className={styles.root}>
      <Box
        padding="none"
        paddingWide="none"
        data-cy="CalendarNavigation"
        className={clsx({
          [styles.calendar]: isSmallViewport,
        })}
      >
        <div ref={calendarNavRef}>
          <div className={styles.stickyCalendar}>
            <DayListNavigation />
          </div>
          <TimeSlotList
            timeslots={timeslots}
            startTimeIndex={startTimeIndex}
            onSelect={handleTimeSlotSelect}
            onGoToNext={(date: string) => {
              handleDaySelect(date);
              trackDateSelectedEvent({
                store,
                isMobile,
                action: 'next available date button',
              });
            }}
            selectedDate={selectedDate}
            initialTimeslotCount={initialDateTimeslots.length}
          />
        </div>
      </Box>
    </div>
  );
};
